/** @jsx isolateComponent */
import { isolateComponent } from '@zapier/style-encapsulation';
import { daysFromNow } from '@zapier/date';
import { Alert, Link } from '@zapier/design-system';
import { Price } from '@zapier/currency';
import { Fragment } from 'react';
import { buildUpgradeUrl, getShouldRenderSunsetCopy } from './utils';
import type { Result } from '../useAlertBanner/alertBannerNotificationsQuery';
import { useUniversalNavContext } from '../../../../universal-nav/hooks';

import { BannerProps } from './types';

export const shouldRender = (data: Result['data']) => {
  const { currentAccount } = data;
  const { isTrialing, trialEndDate } = currentAccount;
  return (
    !isTrialing &&
    !!trialEndDate &&
    currentAccount.plan.amount === 0 &&
    daysFromNow(trialEndDate) <= 14
  );
};

export const Component = function ProfessionalTrialEnded(props: BannerProps) {
  const { getFullyQualifiedURL } = useUniversalNavContext();

  const { data, currency } = props;
  const { currentAccount } = data;
  const upgradeUrl = buildUpgradeUrl({
    currentPlanName: currentAccount.plan.name,
    upgradePlanName: data.nextPlan?.name,
    interval: currentAccount.plan.interval,
  });
  // @ref ul-1: Divide by 12 since `nextPlan` should always be annual.
  const nextPlanAmount = (data.nextPlan?.amount || 0) / 12;
  const pricingText = nextPlanAmount > 0 && (
    <span>
      for as low as <Price currency={currency}>{nextPlanAmount}</Price>/month
    </span>
  );

  const onUpgrade = () => {
    props.trackEvent('Trial: clicked banner', {
      in_trial: false,
    });
  };

  const shouldRenderCopy = getShouldRenderSunsetCopy(
    currentAccount.campaignOffers
  );
  const offer = shouldRenderCopy && currentAccount.campaignOffers[0];

  return (
    <Alert
      layout={props.layout}
      title="Your Professional trial has ended"
      type="info"
    >
      {offer ? (
        <Fragment>
          <Link href={getFullyQualifiedURL('/app/pricing')}>Upgrade now</Link>
          &nbsp;to keep using multi-step Zaps, filters, premium apps, and more.
          Plus get a free year of Zapier when you upgrade by November 14th,
          2022.
        </Fragment>
      ) : (
        <Fragment>
          <Link href={upgradeUrl} onClick={onUpgrade}>
            Upgrade
          </Link>
          {/* Use `&nbsp;` to prevent text wrapping from making "Upgrade" a widow/orphan */}
          &nbsp;{pricingText} to keep using multi-step Zaps, filters, premium
          apps, and more
        </Fragment>
      )}
    </Alert>
  );
};
